import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Loader2, Globe } from "lucide-react";

const FIELDS = {
  hero: [["eyebrow", "Eyebrow", "input"], ["title", "Hero Title", "textarea"], ["subtitle", "Hero Subtitle", "textarea"], ["cta", "Primary CTA", "input"], ["cta2", "Secondary CTA", "input"]],
  about: [["vision", "Corporate Vision", "textarea"], ["mission", "Corporate Mission", "textarea"]],
};

export default function ContentManager() {
  const [content, setContent] = useState({});
  const [lang, setLang] = useState("en");
  const [saving, setSaving] = useState("");

  const load = useCallback(() => api.get("/content").then(({ data }) => setContent(data)).catch((e) => console.error(e)), []);
  useEffect(() => { load(); }, [load]);

  const setVal = (block, field, value) => {
    setContent((c) => ({ ...c, [block]: { ...c[block], [lang]: { ...c[block]?.[lang], [field]: value } } }));
  };
  const setStat = (key, value) => setContent((c) => ({ ...c, stats: { ...c.stats, [key]: value } }));
  const setInfo = (key, value) => setContent((c) => ({ ...c, contact_info: { ...c.contact_info, [key]: value } }));

  const save = async (block) => {
    setSaving(block);
    try {
      await api.put(`/content/${block}`, { value: content[block] });
      toast.success(`${block} updated`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(""); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div><h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B3B39]">Content Manager</h1><p className="text-slate-600 mt-1">Edit homepage content with live Hindi/English sync.</p></div>
        <div className="flex items-center gap-1 p-1 rounded-xl bg-teal-50 border border-teal-200">
          {["en", "hi"].map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`content-lang-${l}`} className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${lang === l ? "bg-[#017471] text-white" : "text-slate-600"}`}>
              <Globe className="w-3.5 h-3.5" /> {l === "en" ? "English" : "हिंदी"}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(FIELDS).map(([block, fields]) => (
          <div key={block} className="oj-card p-6" data-testid={`content-block-${block}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="oj-h3 capitalize">{block} Section</h3>
              <button onClick={() => save(block)} disabled={saving === block} className="oj-btn-gold !py-2.5 !px-5 !text-sm" data-testid={`content-save-${block}`}>
                {saving === block ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
            <div className="space-y-4">
              {fields.map(([field, label, type]) => (
                <div key={field}>
                  <label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">{label}</label>
                  {type === "textarea" ? (
                    <textarea rows={2} value={content[block]?.[lang]?.[field] || ""} onChange={(e) => setVal(block, field, e.target.value)} className="oj-input resize-none" data-testid={`content-${block}-${field}`} />
                  ) : (
                    <input value={content[block]?.[lang]?.[field] || ""} onChange={(e) => setVal(block, field, e.target.value)} className="oj-input" data-testid={`content-${block}-${field}`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Stats */}
        <div className="oj-card p-6">
          <div className="flex items-center justify-between mb-5"><h3 className="oj-h3">Homepage Stats</h3><button onClick={() => save("stats")} disabled={saving === "stats"} className="oj-btn-gold !py-2.5 !px-5 !text-sm" data-testid="content-save-stats">{saving === "stats" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {["solar_capacity", "co2_offset", "projects", "years"].map((k) => (
              <div key={k}><label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">{k.replace(/_/g, " ")}</label><input value={content.stats?.[k] || ""} onChange={(e) => setStat(k, e.target.value)} className="oj-input" data-testid={`content-stat-${k}`} /></div>
            ))}
          </div>
        </div>

        {/* Contact info */}
        <div className="oj-card p-6">
          <div className="flex items-center justify-between mb-5"><h3 className="oj-h3">Contact Information</h3><button onClick={() => save("contact_info")} disabled={saving === "contact_info"} className="oj-btn-gold !py-2.5 !px-5 !text-sm" data-testid="content-save-contact">{saving === "contact_info" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</button></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["phone", "email", "cin", "address"].map((k) => (
              <div key={k} className={k === "address" ? "sm:col-span-2" : ""}><label className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2 block">{k}</label><input value={content.contact_info?.[k] || ""} onChange={(e) => setInfo(k, e.target.value)} className="oj-input" data-testid={`content-info-${k}`} /></div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
